import axios from 'axios';
import { 
    API_BASE_BILLING_URL,
    API_PATIENTS_BILLING_LIST,
    API_PATIENTS_BILLING_CREATE_INVOICE,
    API_PATIENTS_BILLING_CREATE_INVOICE_FROM_ACTIVITIES,
    API_PATIENTS_BILLING_INVOICES_LIST,
    API_PATIENTS_BILLING_INVOICE_DETAILS,
    API_PATIENTS_BILLING_INVOICE_CANCEL,
    API_PATIENTS_BILLING_VALIDATE_INVOICE,
    API_PATIENTS_BILLING_PATIENT_ACTIVITIES,
    API_BILLABLE_ACTIVITIES_LIST,
    API_BILLABLE_ACTIVITIES_DETAILS,
    API_BILLABLE_ACTIVITIES_CREATE,
    API_BILLABLE_ACTIVITIES_MASSIVE,
    API_BILLABLE_ACTIVITIES_UPDATE,
    API_BILLABLE_ACTIVITIES_SUMMARY,
    API_BILLABLE_ACTIVITIES_EXPORT,
    API_BILLABLE_ACTIVITIES_REPRICE,
    API_BILLABLE_ACTIVITIES_NON_BILLABLE,
    API_BILLABLE_ACTIVITIES_DELETE,
    API_BILLABLE_ACTIVITIES_RESTORE,
    API_BILLABLE_ACTIVITIES_VALIDATE_SPLIT,
    API_SALES_ORDERS_LIST,
    API_SALES_ORDERS_DETAILS,
    API_SALES_ORDERS_GENERATE,
    API_SALES_ORDERS_CANCEL,
    API_ODOO_EXPORT_ORDERS,
    API_ODOO_WEBHOOK_STATUS,
    API_ODOO_SYNC_STATUS,
    API_CATALOGS_NON_BILLABLE_REASONS,
    API_CATALOGS_TAX_PROFILES,
    API_CATALOGS_ACTIVITY_TYPES,
    API_CATALOGS_ACTIVITY_STATUSES,
    API_CATALOGS_INVOICE_GROUPING_MODES,
    API_BILLING_CONFIG,
    API_BILLING_PING,
    API_BILLING_STATS
} from '../../../helpers/urlHelper';

/**
 * 🎯 CONSTANTES DE ESTADOS Y CONFIGURACIÓN
 */
export const BILLABLE_ACTIVITY_STATUSES = {
    PENDING: 'pending',
    READY: 'ready',
    SO_CREATED: 'so_created',
    PARTIALLY_INVOICED: 'partially_invoiced',
    FULLY_INVOICED: 'fully_invoiced',
    NON_BILLABLE: 'non_billable',
    DELETED: 'deleted'
};

export const SALES_ORDER_STATUSES = {
    DRAFT: 'draft',
    EXPORTED: 'exported',
    SYNCED: 'synced',
    CANCELLED: 'cancelled'
};

export const ACTIVITY_TYPES = {
    CONSULTATION: 'consultation',
    PROCEDURE: 'procedure',
    TEST: 'test',
    MATERIAL: 'material',
    PACKAGE: 'package',
    OTHER: 'other'
};

export const INVOICE_GROUPING_MODES = {
    PAYER_PATIENT: 'PAYER_PATIENT',
    PAYER_ONLY: 'PAYER_ONLY', 
    SINGLE_INVOICE: 'SINGLE_INVOICE'
};

export const TAX_PROFILES = {
    EXENTO: 'EXENTO',
    IVA4: 'IVA4',
    IVA10: 'IVA10',
    IVA21: 'IVA21'
};

export const BILLING_ROLES = {
    ADMIN_CLINICA: 'admin_clinica',
    RESPONSABLE_FACTURACION: 'responsable_facturacion',
    RECEPCION: 'recepcion',
    MEDICO: 'medico',
    AUDITOR: 'auditor'
};

/**
 * 🏥 HELPERS PARA CONSTRUCCIÓN DE URLs DINÁMICAS
 */
const buildBillableActivityDetailsUrl = (activityId) => `${API_BILLABLE_ACTIVITIES_DETAILS}/${activityId}`;
const buildBillableActivityUpdateUrl = (activityId) => `${API_BILLABLE_ACTIVITIES_UPDATE}/${activityId}`;
const buildBillableActivityNonBillableUrl = (activityId) => `${API_BILLABLE_ACTIVITIES_NON_BILLABLE}/${activityId}/non-billable`;
const buildBillableActivityDeleteUrl = (activityId) => `${API_BILLABLE_ACTIVITIES_DELETE}/${activityId}`;
const buildBillableActivityRestoreUrl = (activityId) => `${API_BILLABLE_ACTIVITIES_RESTORE}/${activityId}/restore`;
const buildSalesOrderDetailsUrl = (orderId) => `${API_SALES_ORDERS_DETAILS}/${orderId}`;
const buildSalesOrderCancelUrl = (orderId) => `${API_SALES_ORDERS_CANCEL}/${orderId}/cancel`;
const buildPatientInvoiceDetailsUrl = (invoiceId) => `${API_PATIENTS_BILLING_INVOICE_DETAILS}/${invoiceId}`;
const buildPatientInvoiceCancelUrl = (invoiceId) => `${API_PATIENTS_BILLING_INVOICE_CANCEL}/${invoiceId}/cancel`;

/**
 * 🔍 QUERY PARAMETERS HELPERS
 */
const buildQueryParams = (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            if (Array.isArray(value)) {
                value.forEach(item => params.append(key, item));
            } else {
                params.append(key, value);
            }
        }
    });
    return params.toString();
};

/**
 * 🔧 CONFIGURACIÓN DEL CLIENTE AXIOS
 */
const createBillingApiClient = () => {
    const client = axios.create({
        baseURL: API_BASE_BILLING_URL,
        timeout: 30000,
        headers: { 'Content-Type': 'application/json' }
    });

    client.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            if (token) config.params = { ...config.params, token };
            return config;
        },
        (error) => Promise.reject(error)
    );

    client.interceptors.response.use(
        (response) => response.data,
        (error) => {
            console.error('Billing API Error:', error);
            if (error.response?.status === 401) {
                localStorage.removeItem('token');
                sessionStorage.removeItem('token');
                window.location.href = '/login';
            }
            return Promise.reject(error.response?.data || error);
        }
    );

    return client;
};

const billingApi = createBillingApiClient();

/**
 * 🧾 PATIENT BILLING SERVICE (FACTURACIÓN POR PACIENTE)
 */
export class PatientBillingService {
    
    static async getPatientActivities(patientId, filters = {}) {
        console.log('🔄 [PatientBillingService] Obteniendo actividades del paciente:', patientId);
        try {
            if (!patientId) return { data: null, error: { code: 'INVALID_PATIENT_ID', message: 'ID de paciente requerido' } };
            const queryParams = buildQueryParams(filters);
            const url = queryParams ? `/patients-billing/patient/${patientId}/activities?${queryParams}` : `/patients-billing/patient/${patientId}/activities`;
            const response = await billingApi.get(url);
            console.log('📡 Respuesta recibida:', response.data?.activities?.length || 0, 'actividades');
            return response;
        } catch (error) {
            console.error('❌ Error:', error);
            return { data: null, error: { code: 'SERVICE_ERROR', message: error.message || 'Error al obtener actividades del paciente' } };
        }
    }
    
    static async getPatientsBilling(filters = {}) {
        console.log('🔄 [PatientBillingService] Obteniendo pacientes con actividades facturables');
        try {
            const queryParams = buildQueryParams(filters);
            const url = queryParams ? `${API_PATIENTS_BILLING_LIST}?${queryParams}` : API_PATIENTS_BILLING_LIST;
            const response = await billingApi.get(url);
            console.log('📡 Respuesta recibida:', response.data?.patients?.length || 0, 'pacientes');
            return response;
        } catch (error) {
            console.error('❌ Error:', error);
            return { data: null, error: { code: 'SERVICE_ERROR', message: error.message || 'Error al obtener pacientes con actividades facturables' } };
        }
    }

    static async createInvoiceFromOriginalActivities(invoiceData) {
        console.log('🧾 [PatientBillingService] Creando factura desde actividades originales (FLUJO UNIFICADO)');
        console.log('📦 Datos:', { activities_count: invoiceData.original_activities?.length || 0, grouping_mode: invoiceData.grouping_mode });
        try {
            const response = await billingApi.post(API_PATIENTS_BILLING_CREATE_INVOICE_FROM_ACTIVITIES, invoiceData);
            console.log('✅ Facturas creadas:', response.data?.created_sales_orders?.length || 0);
            return response;
        } catch (error) {
            console.error('❌ Error creando facturas:', error);
            return { data: null, error: { code: 'CREATE_INVOICE_ERROR', message: error.message || 'Error al crear facturas' } };
        }
    }

    static async createInvoiceFromActivities(invoiceData) {
        console.log('🧾 [PatientBillingService] Creando facturas desde actividades (LEGACY)');
        try {
            const response = await billingApi.post(API_PATIENTS_BILLING_CREATE_INVOICE, invoiceData);
            console.log('✅ Facturas creadas:', response.data?.created_invoices?.length || 0);
            return response;
        } catch (error) {
            console.error('❌ Error creando facturas:', error);
            return { data: null, error: { code: 'CREATE_INVOICE_ERROR', message: error.message || 'Error al crear facturas' } };
        }
    }

    static async getInvoices(filters = {}) {
        console.log('🔄 [PatientBillingService] Obteniendo lista de facturas');
        try {
            const queryParams = buildQueryParams(filters);
            const url = queryParams ? `${API_PATIENTS_BILLING_INVOICES_LIST}?${queryParams}` : API_PATIENTS_BILLING_INVOICES_LIST;
            return await billingApi.get(url);
        } catch (error) {
            console.error('❌ Error obteniendo facturas:', error);
            return { data: null, error: { code: 'GET_INVOICES_ERROR', message: error.message || 'Error al obtener facturas' } };
        }
    }

    static async getInvoiceById(invoiceId) {
        console.log('🔍 [PatientBillingService] Obteniendo detalle de factura:', invoiceId);
        try {
            return await billingApi.get(buildPatientInvoiceDetailsUrl(invoiceId));
        } catch (error) {
            console.error('❌ Error obteniendo detalle de factura:', error);
            return { data: null, error: { code: 'GET_INVOICE_ERROR', message: error.message || 'Error al obtener detalle de factura' } };
        }
    }

    static async cancelInvoice(invoiceId, reason = null) {
        console.log('🗑️ [PatientBillingService] Cancelando factura:', invoiceId);
        try {
            return await billingApi.post(buildPatientInvoiceCancelUrl(invoiceId), { reason });
        } catch (error) {
            console.error('❌ Error cancelando factura:', error);
            return { data: null, error: { code: 'CANCEL_INVOICE_ERROR', message: error.message || 'Error al cancelar factura' } };
        }
    }

    static async validateInvoice(validationData) {
        console.log('✅ [PatientBillingService] Validando datos para factura');
        try {
            return await billingApi.post(API_PATIENTS_BILLING_VALIDATE_INVOICE, validationData);
        } catch (error) {
            console.error('❌ Error validando factura:', error);
            return { data: null, error: { code: 'VALIDATE_INVOICE_ERROR', message: error.message || 'Error al validar datos de factura' } };
        }
    }
}

/**
 * 💰 BILLABLE ACTIVITIES SERVICE
 */
export class BillableActivitiesService {
    
    static async getList(filters = {}) {
        console.log('🔄 [BillableActivitiesService] Iniciando getList');
        try {
            const queryParams = buildQueryParams(filters);
            const url = queryParams ? `${API_BILLABLE_ACTIVITIES_LIST}?${queryParams}` : API_BILLABLE_ACTIVITIES_LIST;
            const response = await billingApi.get(url);
            console.log('📡 Respuesta recibida:', response.data?.items?.length || 0, 'actividades');
            return response;
        } catch (error) {
            console.error('❌ Error:', error);
            return { data: null, error: { code: 'SERVICE_ERROR', message: error.message || 'Error al obtener actividades facturables' } };
        }
    }

    static async getById(activityId) {
        return await billingApi.get(buildBillableActivityDetailsUrl(activityId));
    }

    static async create(activityData) {
        return await billingApi.post(API_BILLABLE_ACTIVITIES_CREATE, activityData);
    }

    static async createMassive(massiveData) {
        console.log('🔄 [BillableActivitiesService] Creación masiva de actividades');
        console.log('📦 Actividades a crear:', massiveData.selected_activities?.length || 0);
        try {
            const response = await billingApi.post(API_BILLABLE_ACTIVITIES_MASSIVE, massiveData);
            console.log('✅ Actividades creadas:', response.data?.created_activities?.length || 0);
            return response;
        } catch (error) {
            console.error('❌ Error en creación masiva:', error);
            return { data: null, error: { code: 'CREATE_MASSIVE_ERROR', message: error.message || 'Error al crear actividades masivamente' } };
        }
    }

    static async update(activityId, activityData) {
        return await billingApi.put(buildBillableActivityUpdateUrl(activityId), activityData);
    }

    static async getSummary(filters = {}) {
        const queryParams = buildQueryParams(filters);
        const url = queryParams ? `${API_BILLABLE_ACTIVITIES_SUMMARY}?${queryParams}` : API_BILLABLE_ACTIVITIES_SUMMARY;
        return await billingApi.get(url);
    }

    static async export(filters = {}, format = 'csv') {
        const queryParams = buildQueryParams({ ...filters, format });
        const url = queryParams ? `${API_BILLABLE_ACTIVITIES_EXPORT}?${queryParams}` : `${API_BILLABLE_ACTIVITIES_EXPORT}?format=${format}`;
        try {
            const response = await fetch(`${API_BASE_BILLING_URL}${url}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Accept': format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `actividades_facturables_${new Date().getTime()}.${format}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            return { data: { message: 'Exportación completada' }, error: null };
        } catch (error) {
            console.error('Error en exportación:', error);
            return { data: null, error: { code: 'EXPORT_ERROR', message: 'Error al exportar actividades' } };
        }
    }

    static async reprice(activityIds, force = false) {
        return await billingApi.post(API_BILLABLE_ACTIVITIES_REPRICE, { ids: activityIds, force });
    }

    static async markNonBillable(activityId, reasonCode, comment = null) {
        return await billingApi.post(buildBillableActivityNonBillableUrl(activityId), { reason_code: reasonCode, comment });
    }

    static async delete(activityId) {
        return await billingApi.delete(buildBillableActivityDeleteUrl(activityId));
    }

    static async restore(activityId) {
        return await billingApi.post(buildBillableActivityRestoreUrl(activityId));
    }

    static async validateSplit(splits) {
        return await billingApi.post(API_BILLABLE_ACTIVITIES_VALIDATE_SPLIT, { splits });
    }

    static getAvailableStatuses() {
        return Object.entries(BILLABLE_ACTIVITY_STATUSES).map(([key, value]) => ({ key, value, label: this.getStatusLabel(value) }));
    }

    static getStatusLabel(status) {
        const labels = {
            [BILLABLE_ACTIVITY_STATUSES.PENDING]: 'Pendiente',
            [BILLABLE_ACTIVITY_STATUSES.READY]: 'Listo para OV',
            [BILLABLE_ACTIVITY_STATUSES.SO_CREATED]: 'OV generada',
            [BILLABLE_ACTIVITY_STATUSES.PARTIALLY_INVOICED]: 'Parcialmente facturada',
            [BILLABLE_ACTIVITY_STATUSES.FULLY_INVOICED]: 'Totalmente facturada',
            [BILLABLE_ACTIVITY_STATUSES.NON_BILLABLE]: 'No facturable',
            [BILLABLE_ACTIVITY_STATUSES.DELETED]: 'Eliminada'
        };
        return labels[status] || status;
    }

    static getStatusColor(status) {
        const colors = {
            [BILLABLE_ACTIVITY_STATUSES.PENDING]: 'gray',
            [BILLABLE_ACTIVITY_STATUSES.READY]: 'blue',
            [BILLABLE_ACTIVITY_STATUSES.SO_CREATED]: 'purple',
            [BILLABLE_ACTIVITY_STATUSES.PARTIALLY_INVOICED]: 'amber',
            [BILLABLE_ACTIVITY_STATUSES.FULLY_INVOICED]: 'green',
            [BILLABLE_ACTIVITY_STATUSES.NON_BILLABLE]: 'red',
            [BILLABLE_ACTIVITY_STATUSES.DELETED]: 'gray'
        };
        return colors[status] || 'gray';
    }

    static canEdit(activity) {
        return ![BILLABLE_ACTIVITY_STATUSES.FULLY_INVOICED, BILLABLE_ACTIVITY_STATUSES.DELETED].includes(activity.status) && 
               !activity.so_links?.some(link => link.exported_at);
    }

    static canMarkNonBillable(activity) {
        return ![BILLABLE_ACTIVITY_STATUSES.FULLY_INVOICED, BILLABLE_ACTIVITY_STATUSES.NON_BILLABLE, BILLABLE_ACTIVITY_STATUSES.DELETED].includes(activity.status);
    }

    static canDelete(activity) {
        return ![BILLABLE_ACTIVITY_STATUSES.FULLY_INVOICED, BILLABLE_ACTIVITY_STATUSES.PARTIALLY_INVOICED, BILLABLE_ACTIVITY_STATUSES.DELETED].includes(activity.status);
    }

    static canRestore(activity) {
        return activity.status === BILLABLE_ACTIVITY_STATUSES.DELETED;
    }
}

/**
 * 📋 SALES ORDERS SERVICE
 */
export class SalesOrdersService {
    
    static async getList(filters = {}) {
        const queryParams = buildQueryParams(filters);
        const url = queryParams ? `${API_SALES_ORDERS_LIST}?${queryParams}` : API_SALES_ORDERS_LIST;
        return await billingApi.get(url);
    }

    static async getById(orderId) {
        return await billingApi.get(buildSalesOrderDetailsUrl(orderId));
    }

    static async generate(generationData) {
        return await billingApi.post(API_SALES_ORDERS_GENERATE, generationData);
    }

    static async cancel(orderId, reason = null) {
        return await billingApi.post(buildSalesOrderCancelUrl(orderId), { reason });
    }

    static getAvailableStatuses() {
        return Object.entries(SALES_ORDER_STATUSES).map(([key, value]) => ({ key, value, label: this.getStatusLabel(value) }));
    }

    static getStatusLabel(status) {
        const labels = {
            [SALES_ORDER_STATUSES.DRAFT]: 'Borrador',
            [SALES_ORDER_STATUSES.EXPORTED]: 'Exportada',
            [SALES_ORDER_STATUSES.SYNCED]: 'Sincronizada',
            [SALES_ORDER_STATUSES.CANCELLED]: 'Cancelada'
        };
        return labels[status] || status;
    }

    static getStatusColor(status) {
        const colors = {
            [SALES_ORDER_STATUSES.DRAFT]: 'blue',
            [SALES_ORDER_STATUSES.EXPORTED]: 'amber',
            [SALES_ORDER_STATUSES.SYNCED]: 'green',
            [SALES_ORDER_STATUSES.CANCELLED]: 'red'
        };
        return colors[status] || 'gray';
    }

    static canCancel(order) {
        return order.status === SALES_ORDER_STATUSES.DRAFT;
    }

    static canExport(order) {
        return order.status === SALES_ORDER_STATUSES.DRAFT;
    }
}

/**
 * 🔗 ODOO INTEGRATION SERVICE
 */
export class OdooIntegrationService {
    
    static async exportSalesOrders(orderIds) {
        return await billingApi.post(API_ODOO_EXPORT_ORDERS, { sales_order_ids: orderIds });
    }

    static async getSyncStatus() {
        return await billingApi.get(API_ODOO_SYNC_STATUS);
    }

    static async handleWebhook(webhookData) {
        return await billingApi.post(API_ODOO_WEBHOOK_STATUS, webhookData);
    }
}

/**
 * 📚 CATALOGS SERVICE
 */
export class CatalogsService {
    
    static async getNonBillableReasons() {
        return await billingApi.get(API_CATALOGS_NON_BILLABLE_REASONS);
    }

    static async getTaxProfiles() {
        return await billingApi.get(API_CATALOGS_TAX_PROFILES);
    }

    static async getActivityTypes() {
        return await billingApi.get(API_CATALOGS_ACTIVITY_TYPES);
    }

    static async getActivityStatuses() {
        return await billingApi.get(API_CATALOGS_ACTIVITY_STATUSES);
    }

    static async getInvoiceGroupingModes() {
        return await billingApi.get(API_CATALOGS_INVOICE_GROUPING_MODES);
    }
}

/**
 * ⚙️ BILLING CONFIGURATION SERVICE
 */
export class BillingConfigService {
    
    static async getConfig() {
        console.log('⚙️ [BillingConfigService] Obteniendo configuración de facturación');
        try {
            return await billingApi.get(API_BILLING_CONFIG);
        } catch (error) {
            console.error('❌ Error obteniendo configuración:', error);
            return { data: null, error: { code: 'CONFIG_ERROR', message: error.message || 'Error al obtener configuración' } };
        }
    }

    static async ping() {
        console.log('🏥 [BillingConfigService] Ping al servicio de facturación');
        try {
            return await billingApi.get(API_BILLING_PING);
        } catch (error) {
            console.error('❌ Error en ping:', error);
            return { data: null, error: { code: 'PING_ERROR', message: error.message || 'Error al conectar con el servicio' } };
        }
    }

    static async getStats() {
        console.log('📊 [BillingConfigService] Obteniendo estadísticas de facturación');
        try {
            return await billingApi.get(API_BILLING_STATS);
        } catch (error) {
            console.error('❌ Error obteniendo estadísticas:', error);
            return { data: null, error: { code: 'STATS_ERROR', message: error.message || 'Error al obtener estadísticas' } };
        }
    }
}

/**
 * 🛠️ BILLING UTILITIES
 */
export class BillingUtils {
    
    /**
     * Formatea un monto a formato moneda
     */
    static formatCurrency(amount, currency = 'USD', locale = 'es-ES') {
        try {
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(amount);
        } catch (error) {
            console.error('Error formateando moneda:', error);
            return `${amount.toFixed(2)} ${currency}`;
        }
    }

    /**
     * Calcula el total de actividades
     */
    static calculateTotal(activities) {
        if (!Array.isArray(activities)) return 0;
        return activities.reduce((sum, activity) => sum + (activity.amount || 0), 0);
    }

    /**
     * Calcula el total de impuestos
     */
    static calculateTaxes(activities, taxProfile) {
        if (!Array.isArray(activities)) return 0;
        
        const taxRates = {
            'EXENTO': 0,
            'IVA4': 0.04,
            'IVA10': 0.10,
            'IVA21': 0.21
        };
        
        const rate = taxRates[taxProfile] || 0;
        const subtotal = this.calculateTotal(activities);
        return subtotal * rate;
    }

    /**
     * Calcula el total con impuestos
     */
    static calculateGrandTotal(activities, taxProfile) {
        const subtotal = this.calculateTotal(activities);
        const taxes = this.calculateTaxes(activities, taxProfile);
        return subtotal + taxes;
    }

    /**
     * Verifica si una actividad puede ser facturada
     */
    static isBillable(activity) {
        return activity.status !== 'non_billable' && 
               activity.status !== 'deleted' && 
               activity.amount > 0;
    }

    /**
     * Agrupa actividades por paciente
     */
    static groupByPatient(activities) {
        return activities.reduce((grouped, activity) => {
            const patientId = activity.patient_id;
            if (!grouped[patientId]) {
                grouped[patientId] = [];
            }
            grouped[patientId].push(activity);
            return grouped;
        }, {});
    }

    /**
     * Agrupa actividades por pagador
     */
    static groupByPayer(activities) {
        return activities.reduce((grouped, activity) => {
            const payerId = activity.payer_id;
            if (!grouped[payerId]) {
                grouped[payerId] = [];
            }
            grouped[payerId].push(activity);
            return grouped;
        }, {});
    }

    /**
     * Agrupa actividades por tipo
     */
    static groupByType(activities) {
        return activities.reduce((grouped, activity) => {
            const type = activity.activity_type || 'OTHER';
            if (!grouped[type]) {
                grouped[type] = [];
            }
            grouped[type].push(activity);
            return grouped;
        }, {});
    }

    /**
     * Genera resumen de actividades
     */
    static generateActivitySummary(activities) {
        return {
            total_activities: activities.length,
            total_amount: this.calculateTotal(activities),
            billable_count: activities.filter(a => this.isBillable(a)).length,
            non_billable_count: activities.filter(a => !this.isBillable(a)).length,
            by_type: this.groupByType(activities),
            by_patient: Object.keys(this.groupByPatient(activities)).length,
            by_payer: Object.keys(this.groupByPayer(activities)).length
        };
    }

    /**
     * Valida datos de factura
     */
    static validateInvoiceData(invoiceData) {
        const errors = [];

        if (!invoiceData.activities || invoiceData.activities.length === 0) {
            errors.push('Se requiere al menos una actividad');
        }

        if (!invoiceData.payer_id) {
            errors.push('El pagador es requerido');
        }

        if (!invoiceData.grouping_mode) {
            errors.push('El modo de agrupación es requerido');
        }

        if (invoiceData.activities && invoiceData.activities.length > 0) {
            const billableActivities = invoiceData.activities.filter(a => this.isBillable(a));
            if (billableActivities.length === 0) {
                errors.push('No hay actividades facturables');
            }
        }

        return {
            valid: errors.length === 0,
            errors
        };
    }

    /**
     * Obtiene el estado siguiente de una actividad
     */
    static getNextStatus(currentStatus) {
        const statusFlow = {
            'pending': 'ready',
            'ready': 'so_created',
            'so_created': 'partially_invoiced',
            'partially_invoiced': 'fully_invoiced'
        };
        return statusFlow[currentStatus] || currentStatus;
    }

    /**
     * Verifica si una factura puede ser cancelada
     */
    static canCancelInvoice(invoice) {
        return invoice.status !== 'cancelled' && 
               invoice.status !== 'synced';
    }

    /**
     * Obtiene la etiqueta de un tipo de actividad
     */
    static getActivityTypeLabel(type) {
        const labels = {
            'consultation': 'Consulta',
            'procedure': 'Procedimiento',
            'test': 'Prueba/Test',
            'material': 'Material',
            'package': 'Paquete',
            'other': 'Otro'
        };
        return labels[type] || type;
    }

    /**
     * Obtiene el color de un tipo de actividad
     */
    static getActivityTypeColor(type) {
        const colors = {
            'consultation': 'blue',
            'procedure': 'purple',
            'test': 'cyan',
            'material': 'amber',
            'package': 'green',
            'other': 'gray'
        };
        return colors[type] || 'gray';
    }

    /**
     * Genera número de factura
     */
    static generateInvoiceNumber(patientId, timestamp = Date.now()) {
        return `INV-${patientId}-${timestamp}`;
    }

    /**
     * Calcula el descuento aplicable
     */
    static calculateDiscount(amount, discountPercentage) {
        if (!discountPercentage || discountPercentage < 0) return 0;
        return (amount * discountPercentage) / 100;
    }

    /**
     * Obtiene resumen de estado de órdenes de venta
     */
    static generateSalesOrdersSummary(salesOrders) {
        return {
            total_orders: salesOrders.length,
            draft_count: salesOrders.filter(o => o.status === 'draft').length,
            exported_count: salesOrders.filter(o => o.status === 'exported').length,
            synced_count: salesOrders.filter(o => o.status === 'synced').length,
            cancelled_count: salesOrders.filter(o => o.status === 'cancelled').length,
            total_amount: salesOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0)
        };
    }

    /**
     * Detecta duplicados en actividades
     */
    static findDuplicateActivities(activities) {
        const seen = new Map();
        const duplicates = [];

        activities.forEach(activity => {
            const key = `${activity.patient_id}-${activity.activity_type}-${activity.amount}-${activity.date}`;
            if (seen.has(key)) {
                duplicates.push({
                    activity1: seen.get(key),
                    activity2: activity
                });
            } else {
                seen.set(key, activity);
            }
        });

        return duplicates;
    }

    /**
     * Exporta actividades a formato CSV
     */
    static exportToCSV(activities, filename = 'actividades.csv') {
        const headers = ['ID', 'Paciente', 'Tipo', 'Monto', 'Estado', 'Fecha'];
        const rows = activities.map(a => [
            a.id,
            a.patient_id,
            a.activity_type,
            a.amount,
            a.status,
            a.date
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }
}

export default {
    PatientBillingService,
    BillableActivitiesService,
    SalesOrdersService,
    OdooIntegrationService,
    CatalogsService,
    BillingConfigService,
    BILLABLE_ACTIVITY_STATUSES,
    SALES_ORDER_STATUSES,
    ACTIVITY_TYPES,
    INVOICE_GROUPING_MODES,
    TAX_PROFILES,
    BILLING_ROLES
};